import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Terminal,
  LogOut,
  Download,
  RefreshCw,
  Users,
  Building2,
  Mail,
  Loader2,
  Sparkles,
  Star,
} from "lucide-react";
import { api, auth, API } from "@/lib/api";
import BrandMark from "@/components/BrandMark";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [waitlist, setWaitlist] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [benchmark, setBenchmark] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [userCases, setUserCases] = useState([]);
  const [stats, setStats] = useState({ waitlist_total: 0, b2b_total: 0, benchmark_total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("pending");

  const fetchAll = async () => {
    try {
      const [w, c, b, g, u, s] = await Promise.all([
        api.get("/admin/waitlist"),
        api.get("/admin/b2b-contacts"),
        api.get("/admin/benchmark"),
        api.get("/admin/benchmark/grouped"),
        api.get("/admin/user-cases"),
        api.get("/admin/stats"),
      ]);
      setWaitlist(w.data);
      setContacts(c.data);
      setBenchmark(b.data);
      setGrouped(g.data);
      setUserCases(u.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const moderate = async (id, action) => {
    try {
      await api.post(`/admin/user-cases/${id}/${action}`);
      setUserCases((cur) =>
        cur.map((r) => (r.id === id ? { ...r, status: action === "approve" ? "approved" : "rejected" } : r))
      );
    } catch {
      /* toast omitted for brevity */
    }
  };

  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    const id = setTimeout(() => fetchAll(), 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRefresh = () => {
    setRefreshing(true);
    fetchAll();
  };

  const onLogout = () => {
    auth.logout();
    navigate("/admin/login", { replace: true });
  };

  const downloadCsv = async (path, filename) => {
    const token = auth.token();
    const res = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <main
      data-testid="admin-dashboard-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F7F6F1] text-zinc-900"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.4]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.06) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse at center, black 45%, transparent 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 45%, transparent 90%)",
        }}
      />
      <div className="grain-overlay" />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between border-b border-zinc-200 bg-white/60 px-6 py-4 backdrop-blur sm:px-10 lg:px-16">
        <Link
          to="/"
          className="flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-600 hover:text-emerald-700"
        >
          <Terminal className="h-3.5 w-3.5 text-emerald-600" />
          <BrandMark size="inline" /> / admin
        </Link>
        <div className="flex items-center gap-3 sm:gap-5">
          <span className="hidden font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500 sm:inline">
            {auth.email()}
          </span>
          <button
            onClick={onRefresh}
            data-testid="refresh-btn"
            className="group inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-700 transition-colors hover:border-emerald-500 hover:text-emerald-700"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="group inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-700 transition-colors hover:border-red-400 hover:text-red-600"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <section className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-24 pt-10 sm:px-10 lg:px-16">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
            // dashboard
          </p>
          <h1
            data-testid="admin-dashboard-title"
            className="font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-zinc-900 sm:text-5xl"
          >
            Signal capture.
          </h1>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-4">
          <StatCard
            icon={<Sparkles className="h-4 w-4" />}
            label="Benchmark submissions"
            value={stats.benchmark_total}
            loading={loading}
          />
          <StatCard
            icon={<Mail className="h-4 w-4" />}
            label="Waitlist emails"
            value={stats.waitlist_total}
            loading={loading}
          />
          <StatCard
            icon={<Building2 className="h-4 w-4" />}
            label="B2B inquiries"
            value={stats.b2b_total}
            loading={loading}
          />
          <StatCard
            icon={<Users className="h-4 w-4" />}
            label="Total signals"
            value={stats.waitlist_total + stats.b2b_total + stats.benchmark_total}
            loading={loading}
          />
        </div>

        {/* Tabs */}
        <div className="mt-12 flex items-center justify-between border-b border-zinc-200">
          <div className="flex flex-wrap">
            <TabButton
              active={tab === "pending"}
              onClick={() => setTab("pending")}
              testId="tab-pending"
            >
              Case reviews ({userCases.filter((c) => c.status === "pending_review").length})
            </TabButton>
            <TabButton
              active={tab === "benchmark"}
              onClick={() => setTab("benchmark")}
              testId="tab-benchmark"
            >
              Benchmark ({stats.benchmark_total})
            </TabButton>
            <TabButton
              active={tab === "grouped"}
              onClick={() => setTab("grouped")}
              testId="tab-grouped"
            >
              Tools grouped ({grouped.length})
            </TabButton>
            <TabButton
              active={tab === "waitlist"}
              onClick={() => setTab("waitlist")}
              testId="tab-waitlist"
            >
              Waitlist ({stats.waitlist_total})
            </TabButton>
            <TabButton
              active={tab === "b2b"}
              onClick={() => setTab("b2b")}
              testId="tab-b2b"
            >
              B2B ({stats.b2b_total})
            </TabButton>
          </div>
          {tab !== "grouped" && (
            <button
              onClick={() => {
                if (tab === "waitlist") downloadCsv("/admin/waitlist.csv", "deployd-waitlist.csv");
                else if (tab === "b2b") downloadCsv("/admin/b2b-contacts.csv", "deployd-b2b-contacts.csv");
                else if (tab === "benchmark") downloadCsv("/admin/benchmark.csv", "deployd-benchmark.csv");
              }}
              data-testid="export-csv-btn"
              className="group mb-2 inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-700 transition-colors hover:border-emerald-500 hover:text-emerald-700"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
          )}
        </div>

        {/* Tables */}
        <div className="mt-6 border border-zinc-200 bg-white">
          {loading ? (
            <div className="flex items-center justify-center gap-3 py-16 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" /> loading...
            </div>
          ) : tab === "pending" ? (
            <PendingCasesTable rows={userCases} onModerate={moderate} />
          ) : tab === "benchmark" ? (
            <BenchmarkTable rows={benchmark} />
          ) : tab === "grouped" ? (
            <GroupedTable rows={grouped} />
          ) : tab === "waitlist" ? (
            <WaitlistTable rows={waitlist} />
          ) : (
            <B2BTable rows={contacts} />
          )}
        </div>
      </section>
    </main>
  );
}

const StatCard = ({ icon, label, value, loading }) => (
  <div className="flex flex-col gap-3 bg-white p-6">
    <div className="flex items-center gap-3 text-emerald-700">{icon}</div>
    <p className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
      {label}
    </p>
    <p className="font-display text-4xl font-extrabold tracking-tight text-zinc-900">
      {loading ? "—" : value}
    </p>
  </div>
);

const TabButton = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`-mb-px border-b-2 px-4 py-3 font-mono-tech text-[11px] uppercase tracking-[0.24em] transition-colors ${
      active
        ? "border-emerald-600 text-zinc-900"
        : "border-transparent text-zinc-500 hover:text-zinc-800"
    }`}
  >
    {children}
  </button>
);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const WaitlistTable = ({ rows }) => {
  if (!rows.length) {
    return <Empty label="No waitlist signups yet." />;
  }
  return (
    <div className="overflow-x-auto" data-testid="waitlist-table">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            <Th>Email</Th>
            <Th>Signed up</Th>
            <Th className="text-right">ID</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.id || i}
              className="border-b border-zinc-100 transition-colors hover:bg-emerald-50/40"
            >
              <Td className="font-display text-base text-zinc-900">{r.email}</Td>
              <Td className="font-mono-tech text-xs text-zinc-600">
                {formatDate(r.created_at)}
              </Td>
              <Td className="text-right font-mono-tech text-[10px] text-zinc-400">
                {r.id?.slice(0, 8)}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const B2BTable = ({ rows }) => {
  if (!rows.length) {
    return <Empty label="No B2B inquiries yet." />;
  }
  return (
    <div className="overflow-x-auto" data-testid="b2b-table">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Company</Th>
            <Th>Submitted</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.id || i}
              className="border-b border-zinc-100 transition-colors hover:bg-emerald-50/40"
            >
              <Td className="font-display text-base text-zinc-900">{r.name}</Td>
              <Td className="font-mono-tech text-xs text-zinc-700">{r.email}</Td>
              <Td className="font-display text-base text-zinc-900">{r.company}</Td>
              <Td className="font-mono-tech text-xs text-zinc-600">
                {formatDate(r.created_at)}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const labelize = (s) => (s || "").replace(/_/g, " ");

const BenchmarkTable = ({ rows }) => {
  if (!rows.length) return <Empty label="No benchmark submissions yet." />;
  return (
    <div className="overflow-x-auto" data-testid="benchmark-table">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            <Th>Tool</Th>
            <Th>Use cases</Th>
            <Th>Rating</Th>
            <Th>Impact</Th>
            <Th>Buy again</Th>
            <Th>Peer advice</Th>
            <Th>Email</Th>
            <Th>Submitted</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.id || i}
              className="border-b border-zinc-100 align-top transition-colors hover:bg-emerald-50/40"
            >
              <Td>
                <div className="font-display text-base text-zinc-900">{r.tool_raw}</div>
                <div className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-400">
                  {r.tool_normalized}
                </div>
              </Td>
              <Td className="font-mono-tech text-xs text-zinc-700">
                {(r.use_cases || []).map(labelize).join(", ")}
              </Td>
              <Td>
                <span className="inline-flex items-center gap-1 font-display text-lg font-bold text-zinc-900">
                  <Star className="h-3.5 w-3.5 text-emerald-600" />
                  {r.promise_rating}
                </span>
              </Td>
              <Td className="font-mono-tech text-xs text-zinc-700">
                {(r.impact_areas || []).map(labelize).join(", ")}
              </Td>
              <Td className="font-mono-tech text-xs text-zinc-700">
                {labelize(r.would_buy_again)}
              </Td>
              <Td className="max-w-xs whitespace-normal text-sm italic text-zinc-700">
                {r.peer_advice ? `"${r.peer_advice}"` : <span className="text-zinc-300">—</span>}
              </Td>
              <Td className="font-mono-tech text-xs text-zinc-700">{r.email}</Td>
              <Td className="font-mono-tech text-xs text-zinc-600">
                {formatDate(r.created_at)}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const GroupedTable = ({ rows }) => {
  if (!rows.length) return <Empty label="No data to group yet." />;
  return (
    <div className="overflow-x-auto" data-testid="grouped-table">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            <Th>Tool (normalized)</Th>
            <Th>Mentions</Th>
            <Th>Avg rating</Th>
            <Th>Raw variants seen</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.tool_normalized || i}
              className="border-b border-zinc-100 transition-colors hover:bg-emerald-50/40"
            >
              <Td className="font-display text-base text-zinc-900">{r.tool_normalized || "—"}</Td>
              <Td className="font-display text-lg font-bold text-emerald-700">{r.count}</Td>
              <Td className="font-display text-base text-zinc-900">
                {r.avg_rating ? r.avg_rating.toFixed(2) : "—"}
              </Td>
              <Td className="text-sm text-zinc-700">
                {(r.raw_variants || []).join(" · ")}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th
    className={`px-5 py-3 text-left font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500 ${className}`}
  >
    {children}
  </th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-5 py-4 ${className}`}>{children}</td>
);

const Empty = ({ label }) => (
  <div className="flex items-center justify-center py-16 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-400">
    {label}
  </div>
);

const PendingCasesTable = ({ rows, onModerate }) => {
  if (!rows.length) return <Empty label="No user-submitted cases yet." />;
  const statusBadge = {
    pending_review: "bg-amber-50 border-amber-500/50 text-amber-800",
    approved: "bg-emerald-50 border-emerald-500/50 text-emerald-800",
    rejected: "bg-red-50 border-red-300 text-red-700",
  };
  return (
    <div className="overflow-x-auto" data-testid="pending-cases-table">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            <Th>Vendor → Customer</Th>
            <Th>Industry · Size</Th>
            <Th>Problem / ROI</Th>
            <Th>Submitted by</Th>
            <Th>Status</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-zinc-100 align-top">
              <Td>
                <div className="font-display text-base font-bold text-zinc-900">{r.customer}</div>
                <div className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  {r.vendor}
                </div>
              </Td>
              <Td className="font-mono-tech text-xs text-zinc-700">
                {r.industry || "—"}
                <div className="text-zinc-500">{r.size || ""}</div>
              </Td>
              <Td className="max-w-md whitespace-normal text-sm text-zinc-700">
                <div className="line-clamp-2">{r.problem}</div>
                {r.roi && (
                  <div className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-emerald-700">
                    ROI: {r.roi}
                  </div>
                )}
              </Td>
              <Td className="font-mono-tech text-xs text-zinc-700">{r.submitted_by_email}</Td>
              <Td>
                <span
                  className={`inline-flex border px-2 py-1 font-mono-tech text-[10px] uppercase tracking-[0.22em] ${
                    statusBadge[r.status] || "border-zinc-300 bg-white text-zinc-600"
                  }`}
                >
                  {(r.status || "").replace("_", " ")}
                </span>
              </Td>
              <Td className="text-right">
                {r.status === "pending_review" ? (
                  <div className="inline-flex items-center gap-2">
                    <button
                      onClick={() => onModerate(r.id, "approve")}
                      data-testid={`approve-${r.id}`}
                      className="border border-emerald-600 bg-emerald-600 px-3 py-1.5 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-emerald-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => onModerate(r.id, "reject")}
                      data-testid={`reject-${r.id}`}
                      className="border border-zinc-300 bg-white px-3 py-1.5 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-700 transition-colors hover:border-red-400 hover:text-red-600"
                    >
                      Reject
                    </button>
                  </div>
                ) : (
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-400">
                    reviewed
                  </span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
